import {
    AfterViewInit,
    Component,
    DestroyRef,
    ElementRef,
    EventEmitter,
    Input,
    inject,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {MeasurementDataset} from "../base-measurement-chart";
import {DashboardContext} from "../../dashboard/dashboard.context";
import {ChartDataProvider, DashboardTime} from "../../../utils/chart-data-provider";
import moment from "moment";
import lodash from "lodash";
import {ChartCompare} from "../../dashboard/dashboard.types";
import {trackByIndex} from "../../../common/utils";
import {View} from "../../../common/view";
import {Handler} from "../../../common/handler";
import {DataQueryFilters} from "../../measurements-component/measurements-handler.component";
import {ChartUtilsService} from "../chart-utils.service";
import {Measurements} from "../../measurements-component/measurements";

@Component({
    selector: 'app-chart-table',
    templateUrl: './chart-table.component.html',
    styleUrls: ['./chart-table.component.scss']
})
@Handler()
export class ChartTableComponent extends View implements OnChanges, OnInit, AfterViewInit {
    private viewDestroyed = false;
    protected readonly trackByIndex = trackByIndex;
    context = DashboardContext;
    @Input() dataProvider: ChartDataProvider;
    @Input() compare: ChartCompare;
    @Input() unitOverride: string;
    @Input() comparisonLabels?: ChartTableComparisonLabels | null;
    _data: MeasurementDataset[];
    headerTop: string;
    calculatedColumnTimes: ColumnTime[] = [];

    currentPeriod: MeasurementDataset[] = [];
    previousComparePeriod: MeasurementDataset[] = [];
    filters: DataQueryFilters;
    @Input() displayTimeRange: DashboardTime;

    @ViewChild("table") table: ElementRef;
    @Output() tableElement: EventEmitter<ElementRef> = new EventEmitter<ElementRef>();

    private _splitOffPeak = false;

    constructor() {
        super();
        inject(DestroyRef).onDestroy(() => this.viewDestroyed = true);
    }

    @Input()
    set splitOffPeak(value: boolean) {
        this._splitOffPeak = value ?? false;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ((changes['displayTimeRange'] || changes['compare']) && (this.displayTimeRange || this.filters?.timeRange)) {
            this.calculatedColumnTimes = this.columnTimes();
        }
    }

    ngOnInit(): void {
        this.subscribeTo("getChartDataQueryFilters").subscribe((f: DataQueryFilters) => this.filters = f);
        this.dataProvider.aggregatedDataChanged.subscribe(d => {
            this._data = d.datasets.filter(d => !d.dataset.hidden);
            this.calculatedColumnTimes = this.columnTimes();
            const periods = splitChartTableDatasets(this._data);
            this.currentPeriod = periods.current;
            this.previousComparePeriod = periods.previous;
            setTimeout(() => this.headerTop = this.getHeaderTop() + "px", 10);
        });
    }

    ngAfterViewInit() {
        setTimeout(() => this.headerTop = this.getHeaderTop() + "px", 1000);
        // Export controls have already been checked while this child view was created.
        // Publish readiness after that pass, and never publish a detached table after navigation.
        Promise.resolve().then(() => {
            if (!this.viewDestroyed) this.tableElement.emit(this.table);
        });
    }

    records(dataset: MeasurementDataset[], previousDatasets: MeasurementDataset[]): Data[] {
        const slots = this.rows();
        return slots.map((r, i) => {
            let periodChange = false;
            if (slots[i - 1]) {
                periodChange = moment(slots[i - 1].startTime).year() < moment(r.startTime).year()
            }
            return {
                label: r.tableHeader,
                periodChange: periodChange,
                data: dataset
                    .map((d, j) => {
                        const prevDataset = previousDatasets.find(ds => ds.entityId === d.entityId && ds.measurementType == d.measurementType);
                        const val: number = (d.dataset.tooltip.valuesInverted ?
                            (-(d.dataset.tooltip?.data?.[i] ?? d.dataset.data[i])) :
                            (d.dataset.tooltip?.data?.[i] ?? d.dataset.data[i])) as number;
                        const prevVal: number = (prevDataset
                            ? (prevDataset.dataset.tooltip.valuesInverted
                                ? (-(prevDataset.dataset.tooltip?.data?.[i] ?? prevDataset.dataset.data[i]))
                                : (prevDataset.dataset.tooltip?.data?.[i] ?? prevDataset.dataset.data[i]))
                            : 0) as number;
                        let diffPercentage = lodash.round(((val - prevVal) / prevVal) * 100, 1);
                        const percentage = lodash.round(diffPercentage, 1);
                        return <Record>{
                            val: val,
                            prevVal: prevVal,
                            digitsInfo: ChartUtilsService.fractionDigitsForUnit(this.getUnitLabel(d)),
                            percentage: percentage,
                            finite: lodash.isFinite(diffPercentage) && val !== 0,
                            cssClass: this.comparisonLabels ? '' : percentage > 0
                                ? (d.dataset.tooltip.valuesInverted || d.dataset.tooltip.higherValueIsBetter ? 'text-positive' : 'text-danger')
                                : (percentage < 0 ? (d.dataset.tooltip.valuesInverted || d.dataset.tooltip.higherValueIsBetter ? 'text-danger' : 'text-positive') : '')
                        };
                    })
            }
        });
    }

    columns = () => this.currentPeriod.map(
        r => `${this.getLabel(r)}${this.getUnitLabel(r) ? " (" + this.getUnitLabel(r) + ")" : ""}`);

    private getUnitLabel(r: MeasurementDataset): string | null {
        return this.unitOverride || r.displayUnit || (r.measurementType ? this.context.getMeasurementUnit(r.measurementType) : null);
    }

    getLabel = (r: MeasurementDataset) => {
        const hasIntermediateData: boolean = this.currentPeriod.some(d => d.measurementType?.toLowerCase().includes('intermediate'));
        if (hasIntermediateData || !this._splitOffPeak) {
            return r.dataset.label.replace(/\s*\b(?:normal|normaal)\b/i, '').trim();
        }
        return r.dataset.label
    };

    rows = () => Measurements.getSlots(this.displayTimeRange || this.filters.timeRange);

    columnTimes = (): ColumnTime[] => {
        const displayTimeRange = this.displayTimeRange || this.filters.timeRange;
        const timeRangeForYear: DashboardTime = ChartUtilsService.getForYear(displayTimeRange, ChartUtilsService.getComparedYear(this.compare, displayTimeRange));
        const previousTimeRange = Measurements.getSlots(timeRangeForYear).map(s => moment(s.startTime).year());
        return Measurements.getSlots(displayTimeRange)
            .map((s, i) => (<ColumnTime>{
                previous: previousTimeRange[i],
                current: moment(s.startTime).year()
            }));
    }

    getHeaderTop = () => this.elementRef ? $(this.elementRef.nativeElement).find('#top-header').position()?.top : 0;

    trackBy: any = (index: number, item: any) => item?.label;
}

export const chartTableDatasetOrder = (dataset: MeasurementDataset): number =>
    dataset.dataset.tooltip?.order ?? dataset.dataset.order ?? 0;

export const compareChartTableDatasets = (left: MeasurementDataset, right: MeasurementDataset): number => {
    const displayOrder = chartTableDatasetOrder(left) - chartTableDatasetOrder(right);
    return displayOrder || Number(left.dataset.order || 0) - Number(right.dataset.order || 0);
};

export const splitChartTableDatasets = (datasets: MeasurementDataset[]) => ({
    current: [...datasets]
        .filter(dataset => (dataset.comparisonStack ?? dataset.dataset.stack) === DashboardContext.stacks.currentPeriod
            && dataset.dataset.yAxisID !== 'yScatter')
        .sort(compareChartTableDatasets),
    previous: [...datasets]
        .filter(dataset => (dataset.comparisonStack ?? dataset.dataset.stack) === DashboardContext.stacks.lastYear)
        .sort(compareChartTableDatasets)
});

interface ColumnTime {
    previous: number;
    current: number;
}

export interface ChartTableComparisonLabels {
    previous: string;
    current: string;
    difference: string;
}

interface Data {
    label: string;
    periodChange: boolean;
    data: Record[];
}

interface Record {
    val: number;
    prevVal: number;
    digitsInfo?: string;
    percentage: number;
    finite: boolean;
    cssClass: string;
}
